import styled from '@emotion/styled';
import { List, ListItem, TextField, Typography } from '@material-ui/core';
import { createElement, FC, Fragment, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatPlaybackTime,
  File as UploadedFile,
} from '../../../../../graphql/generated/graphql';
import { SPACING_UNIT } from '../../../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../../../styleUtility/gridLayout';
import CreativeAttributeFileName from '../CreativeAttributeFileName';
import CreativeAttributeTable from '../CreativeAttributeTable';
import { DisplayType } from '../DeliverableDetail';
import _AttachmentDescription from './AttachmentDescription';
import AttachmentSubmissionForm from './AttachmentSubmissionForm';
import _CreativeSubmissionForm from './CreativeSubmissionForm';
import _SubmitButton from './SubmitButton';
import { useUploadAttachment } from './useUploadAttachment';
import { useUploadCreative } from './useUploadCreative';

interface Props {
  creativeFormat: Pick<CreativeFormat, 'id'> & {
    creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
      fastStart?: CreativeFormatFastStart | null;
    };
  };
  canceled: boolean;
  hasDeliverables: boolean;
  type: DisplayType;
  submit: (
    intent: string,
    file: UploadedFile,
    attachedThumbnail: UploadedFile | null,
    onSubmitted: (param: string) => void,
  ) => void;
  submitting: boolean;
  onSubmit: (param: string) => void;
  className?: string;
}

const SubmissionForm: FC<Props> = ({
  creativeFormat,
  canceled,
  hasDeliverables,
  type,
  submit,
  submitting,
  onSubmit,
  className,
}) => {
  const [intent, setIntent] = useState('');
  const {
    upload: uploadCreative,
    uploadedFile: uploadedCreativeFile,
    uploading: uploadingCreative,
  } = useUploadCreative(creativeFormat.creativeType.key);
  const {
    upload: uploadAttachment,
    uploadedFile: uploadedAttachment,
    uploading: uploadingAttachment,
  } = useUploadAttachment();

  const _onSubmit = (param: string) => {
    setIntent('');
    uploadCreative(null);
    uploadAttachment(null);
    onSubmit(param);
  };

  // エラーメッセージ作成
  const errorMessageItem = useMemo(() => {
    const errors: string[] = [];
    // 納品物/制作意図のどれか一つでも空か
    if (!uploadedCreativeFile || !intent) {
      errors.push('show_creation_page.error_unsupported_items');
    }
    return errors.map((val, i) => (
      <MessageItem key={i}>
        <FormattedMessage id={val} />
      </MessageItem>
    ));
  }, [uploadedCreativeFile, intent]);

  const showAttachmentSubmissionForm = creativeFormat.creativeType.key === 'video';
  const Submit = styled(_SubmitButton)(gridLayoutItem('submit'), { justifySelf: 'end', marginTop: SPACING_UNIT });

  return (
    <Root className={className}>
      <CreativeSubmissionForm
        uploadedFile={uploadedCreativeFile || null}
        uploadCreative={uploadCreative}
        uploadingCreative={uploadingCreative}
        className={className}
      />

      <CreativeAttribute>
        <CreativeAttributeFileName file={uploadedCreativeFile || null} />
        <CreativeAttributeTable file={uploadedCreativeFile} creativeFormat={creativeFormat} />
      </CreativeAttribute>
      {showAttachmentSubmissionForm ? (
        <Fragment>
          <Attachment>
            <AttachmentHeading variant="subtitle1">
              <FormattedMessage id="submission_attachment.type.thumbnail_image" />
            </AttachmentHeading>
            <Typography>
              <FormattedMessage id="submission_attachment.type.thumbnail_image.message" />
            </Typography>
            <AttachmentDescription canceled={canceled} hasDeliverables={hasDeliverables} />
            <AttachmentSubmissionForm
              uploadAttachment={uploadAttachment}
              uploadingAttachment={uploadingAttachment}
              uploadedAttachment={uploadedAttachment || null}
              className={className}
            />
          </Attachment>
        </Fragment>
      ) : null}

      <Intent>
        <IntentHeading variant="subtitle1" noWrap>
          <FormattedMessage id={`deliverable.intent_heading_${type}`} />
        </IntentHeading>
        <div>
          <IntentTextHeading>
            <FormattedMessage id={`intent_text_list.header`} />
          </IntentTextHeading>
          <IntentTextList disablePadding={true}>
            <FormattedMessage id={`intent_text_list.item01`}>
              {(msg: string) =>
                msg.trim() !== '' && (
                  <IntentTextListItem disableGutters={true} button>
                    <Typography>{msg}</Typography>
                  </IntentTextListItem>
                )
              }
            </FormattedMessage>
            <FormattedMessage id={`intent_text_list.item02`}>
              {(msg: string) =>
                msg.trim() !== '' && (
                  <IntentTextListItem disableGutters={true} button>
                    <Typography>{msg}</Typography>
                  </IntentTextListItem>
                )
              }
            </FormattedMessage>
            <FormattedMessage id={`intent_text_list.item03`}>
              {(msg: string) =>
                msg.trim() !== '' && (
                  <IntentTextListItem disableGutters={true} button>
                    <Typography>{msg}</Typography>
                  </IntentTextListItem>
                )
              }
            </FormattedMessage>
            <FormattedMessage id={`intent_text_list.item04`}>
              {(msg: string) =>
                msg.trim() !== '' && (
                  <IntentTextListItem disableGutters={true} button>
                    <Typography>{msg}</Typography>
                  </IntentTextListItem>
                )
              }
            </FormattedMessage>
            <FormattedMessage id={`intent_text_list.item05`}>
              {(msg: string) =>
                msg.trim() !== '' && (
                  <IntentTextListItem disableGutters={true} button>
                    <Typography>{msg}</Typography>
                  </IntentTextListItem>
                )
              }
            </FormattedMessage>
          </IntentTextList>
        </div>
        <FormattedMessage id="intent_input.placeholder">
          {(placeholder: string) => (
            <IntentInput
              id="intent"
              multiline
              rows={8}
              rowsMax={18}
              placeholder={placeholder !== 'intent_input.placeholder' ? placeholder : ''}
              defaultValue={intent}
              onChange={(e) => setIntent(e.currentTarget.value)}
            />
          )}
        </FormattedMessage>
      </Intent>
      <MessageItemWrap>{errorMessageItem}</MessageItemWrap>
      <Submit
        isProcessing={submitting}
        onSubmit={() => {
          if (uploadedCreativeFile) {
            submit(intent, uploadedCreativeFile, uploadedAttachment || null, _onSubmit);
          }
        }}
        disabled={errorMessageItem.length > 0 || !uploadedCreativeFile}
      />
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['1fr', `${SPACING_UNIT * 5}px`, '1fr'],
    rows: ['auto', 'auto', 'auto', 'auto', 'auto'],
    areas: [
      ['creativeSubmissionForm', '.', 'creativeAttribute'],
      ['attachment', 'attachment', 'attachment'],
      ['intent', 'intent', 'intent'],
      ['message', 'message', 'message'],
      ['submit', 'submit', 'submit'],
    ],
  }),
);

const CreativeSubmissionForm = styled(_CreativeSubmissionForm)(gridLayoutItem('creativeSubmissionForm'));

const CreativeAttribute = styled('div')(gridLayoutItem('creativeAttribute'));

const Attachment = styled('div')(gridLayoutItem('attachment'), {
  marginTop: SPACING_UNIT * 4,
});

const AttachmentHeading = styled(Typography)({});

const AttachmentDescription = styled(_AttachmentDescription)({
  marginBottom: SPACING_UNIT * 2,
});

const Intent = styled('div')(gridLayoutItem('intent'), {
  marginTop: SPACING_UNIT * 4,
});

const MessageItemWrap = styled('div')(
  gridLayoutItem('message'),
  `
  justify-self: end;
  margin-top: ${SPACING_UNIT * 4}px;
  color: #FF0000;
`,
);

const MessageItem = styled('div')`
  margin-top: ${SPACING_UNIT}px;
  margin-bottom: ${SPACING_UNIT}px;
`;

const IntentHeading = styled(Typography)({
  marginBottom: SPACING_UNIT * 2,
});

const IntentTextHeading = styled(Typography)({ marginBottom: SPACING_UNIT });

const IntentTextList = styled(List)({ marginBottom: SPACING_UNIT });

const IntentTextListItem = styled(ListItem)({
  display: 'list-item',
  margin: `0 ${SPACING_UNIT * 2}px`,
  padding: `6px 0`,
  listStyleType: 'decimal',
});

const IntentInput = styled(TextField)(gridLayoutItem('intentBody'), { width: '100%', marginBottom: SPACING_UNIT });

export default SubmissionForm;
